import { StudioProjectVersion } from '../../../../../server/graphql/__generated.types';
import { UserContextQuery } from '../../../../context/user/operation.generated';
import {
  getProjectVersionInfo,
  isAzureVoiceProjectVersion,
} from '../../../../util/projectVersioningUtils';

type StudioProjectVersions = UserContextQuery['user']['studioProjectVersions'];

export interface useProjectVersionProps {
  userVersions: StudioProjectVersions;
  enabledVersions?: StudioProjectVersions;
  isAdditionalLanguagesUpgrade?: boolean;
}

type ProjectVersionOptionVariant = 'primary' | 'secondary' | 'tertiary';

export const AZURE_VERSION_PLACEHOLDER = 'azure';
type OtherLanguagesProjectVersionPlaceholder = 'azure';

type Hz = 24000 | 48000 | 96000;

export type SampleRateSubOption = {
  kind: 'samplerate';
  label: string;
  value: Hz;
  description: string;
  disabled?: boolean;
};

export type VersionSubOption = {
  kind: 'version';
  id: StudioProjectVersion;
  label: string;
  disabled: boolean;
};

export type SubOption = VersionSubOption | SampleRateSubOption;

export interface ProjectVersionOptions {
  id: StudioProjectVersion | OtherLanguagesProjectVersionPlaceholder;
  label: string;
  value: number;
  disabled: boolean;
  variant: ProjectVersionOptionVariant;
  options?: Array<SubOption>;
}

export const sampleRateOptions: SampleRateSubOption[] = [
  {
    kind: 'samplerate',
    label: '24kHz',
    value: 24000 as Hz,
    description: "Standard quality you're used to.",
  },
  {
    kind: 'samplerate',
    label: '48kHz',
    value: 48000 as Hz,
    description: 'Higher quality, may be slower to render.',
  },
  {
    kind: 'samplerate',
    label: '96kHz',
    value: 96000 as Hz,
    description:
      'Highest quality, may be slower to render. Supports .wav files only.',
  },
];

export const getAzureVersions = (
  versions: useProjectVersionProps['userVersions']
) => {
  let azureStartIndex = -1;
  const azureVersions = versions.filter((v, index) => {
    if (!isAzureVoiceProjectVersion(v)) return;
    if (azureStartIndex === -1) azureStartIndex = index;
    return v;
  });
  return { azureStartIndex, azureVersions: azureVersions.sort() };
};

export const getSortedVersions = (
  versions: useProjectVersionProps['userVersions']
) => {
  const rank = new Map<StudioProjectVersion | string, number>([
    [StudioProjectVersion.Universal as unknown as string, 0],
    [StudioProjectVersion.Legacy as unknown as string, 1],
  ]);
  return versions.sort(
    (a, b) => (rank.get(a as any) ?? 2) - (rank.get(b as any) ?? 2)
  );
};

/**
 * Resolves `studioVersion` for a selected model index (shared by modal + tests).
 * The Legacy Global row uses a synthetic id (`azure`) until a concrete voice is chosen;
 * callers that must send a real `StudioProjectVersion` to the API cast when that
 * placeholder is still the correct representation of “card selected, language pending”.
 */
export function getStudioVersionFromModel(options: {
  models: ProjectVersionOptions[];
  index: number;
  /**
   * Subset of versions the current context (e.g. folder) allows. Used when the UI shows
   * the global card but does not list per-language options (e.g. upgrade-only row).
   */
  enabledVersions?: StudioProjectVersion[];
  /** Present when the user picked a language in the Legacy Global dropdown—must win over the placeholder. */
  explicitVersion?: StudioProjectVersion;
}) {
  const { models, index, enabledVersions, explicitVersion } = options;

  // Dropdown change supplies the real enum directly; do not re-derive from the card index.
  if (explicitVersion) {
    return explicitVersion;
  }

  const modelOption = models.at(index);
  if (!modelOption) return undefined;

  // “Other languages” is one radio row backed by many Azure voice options. When exactly one
  // language is available and enabled, there is no meaningful choice—lock to that version so
  // create flow and GraphQL never stay on the `azure` placeholder (sync + validation).
  if (modelOption.id === AZURE_VERSION_PLACEHOLDER) {
    const enabledAzureOptions = (modelOption.options ?? []).filter(
      (o): o is VersionSubOption => (o as any).kind === 'version' && !o.disabled
    );

    if (enabledAzureOptions.length === 1) {
      return enabledAzureOptions[0].id;
    }
  }

  // Upsell/disabled tertiary row: no `options` array on the model, but the folder (or similar)
  // only allows a single global voice—map that constraint to the one concrete version.
  if (
    modelOption.id === AZURE_VERSION_PLACEHOLDER &&
    enabledVersions?.length === 1
  ) {
    return enabledVersions[0];
  }

  // Primary/secondary cards use real `StudioProjectVersion` ids. For Legacy Global with multiple
  // languages (or none enabled), keep `azure` so the form still means “this card” until a language is picked.
  return modelOption.id as StudioProjectVersion;
}

const useProjectVersion = () => {
  const getProjectVersionOptions = (options: useProjectVersionProps) => {
    const {
      enabledVersions: initialEnabledVersions,
      userVersions: initialVersions,
      isAdditionalLanguagesUpgrade,
    } = options;
    const userVersions = [...initialVersions];
    const enabledVersions = initialEnabledVersions
      ? [...initialEnabledVersions]
      : undefined;

    const sorted = getSortedVersions(userVersions);
    const availableVersions = sorted.filter(
      v => !isAzureVoiceProjectVersion(v)
    );

    const azureVersions = sorted.filter(isAzureVoiceProjectVersion);

    const isEnabledVersion = (v: StudioProjectVersion) =>
      enabledVersions ? !enabledVersions.includes(v) : false;

    const primaryModels = availableVersions.map<ProjectVersionOptions>(
      (v, index) => {
        const versionInfo = getProjectVersionInfo(v);
        return {
          id: v,
          label: versionInfo.displayName,
          value: index,
          disabled: isEnabledVersion(v),
          variant: v === StudioProjectVersion.V2 ? 'secondary' : 'primary',
        };
      }
    );

    const mapped: VersionSubOption[] = azureVersions.map(v => ({
      kind: 'version',
      id: v,
      label: getProjectVersionInfo(v).displayName,
      disabled: isEnabledVersion(v),
    }));

    const otherLanguagesOptions = mapped.sort((a, b) => {
      if (a.disabled && !b.disabled) return 1;
      if (!a.disabled && b.disabled) return -1;
      return a.label.localeCompare(b.label);
    });

    let otherLanguages: ProjectVersionOptions | undefined;
    if (otherLanguagesOptions.length > 0) {
      otherLanguages = {
        id: AZURE_VERSION_PLACEHOLDER,
        label: 'Other languages',
        value: primaryModels.length,
        disabled: otherLanguagesOptions.every(o => o.disabled),
        variant: 'tertiary',
        options: otherLanguagesOptions,
      };
    }

    if (isAdditionalLanguagesUpgrade && otherLanguages === undefined) {
      otherLanguages = {
        id: AZURE_VERSION_PLACEHOLDER,
        label: 'Other languages',
        value: primaryModels.length,
        disabled: true,
        variant: 'tertiary',
      };
    }

    const modelOptions = primaryModels.concat(otherLanguages || []);
    return modelOptions;
  };

  const getDefaultActiveModel = (
    options: ReturnType<typeof getProjectVersionOptions>
  ) => options.findIndex(o => !o.disabled);

  return {
    getProjectVersionOptions,
    getStudioVersionFromModel,
    getDefaultActiveModel,
  };
};

export default useProjectVersion;
